import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { TFunction } from 'i18next';
import Tooltip from 'react-tooltip-lite';
import { formatNumericValue } from 'utils/general';

import ApprovalStatusPopover from '@/modules/workingDayReconciliation/components/CrewLevel/DetailedCard/ApprovalStatusPopover';
import { IReconciliation } from '@/modules/workingDayReconciliation/types';
import { Typography } from '@/shared/components/atoms';
import { APPROVAL_STATUS } from '@/shared/constant/backendStatus';

import ExleminationMark from '~/images/shared/ExleminationMark.svg';

export const checkConflict = ({
  supplierAssignmentStatus,
  pricePerUnit,
}: IReconciliation.IGetWorkdaySupplierDetails):
  | 'noSupplierAssigmnet'
  | 'unitMismatch'
  | null => {
  if (supplierAssignmentStatus === 0) {
    return 'noSupplierAssigmnet';
  }
  if (supplierAssignmentStatus === 1 && pricePerUnit === 0) {
    return 'unitMismatch';
  }

  return null;
};
const getRenderedCellValue = (value: string | number | undefined | null) =>
  typeof value === 'string' ? value : value ? formatNumericValue(value) : '-';

const columnHelper =
  createColumnHelper<IReconciliation.IGetWorkdaySupplierDetails>();

export const WorkdaySupplierDetailsTableColumns = (
  t: TFunction<'common', undefined>,
  currency: string
) => [
  columnHelper.accessor('jobInstanceId', {
    cell: ({ getValue }) => <span>{getRenderedCellValue(getValue())}</span>,
    header: () => t('job-order'),
  }),
  columnHelper.accessor('category', {
    cell: ({ getValue }) => <span>{t(getValue() || '-')}</span>,
    header: () => t('category'),
  }),
  columnHelper.accessor('place', {
    cell: ({ getValue }) => <span>{getRenderedCellValue(getValue())}</span>,
    header: () => t('location'),
  }),
  columnHelper.accessor('supervisorName', {
    cell: ({ getValue }) => (
      <div className='max-w-[105px] truncate'>
        {getRenderedCellValue(getValue())}
      </div>
    ),
    header: () => t('supervisorName'),
  }),
  columnHelper.accessor('jobName', {
    cell: ({ getValue }) => <span>{getRenderedCellValue(getValue())}</span>,
    header: () => t('jobName'),
  }),
  // columnHelper.accessor('cropName', {
  //   cell: ({ getValue }) => <span>{getRenderedCellValue(getValue())}</span>,
  //   header: () => t('the-type'),
  // }),

  columnHelper.accessor('supplierAssignmentId', {
    cell: ({ getValue }) => <span>{getRenderedCellValue(getValue())}</span>,
    header: () => t('contract-code'),
  }),
  columnHelper.accessor('pricingMethod', {
    cell: ({ getValue }) => (
      <span>{t(getValue() === 'unit' ? 'rate' : getValue() || '-')}</span>
    ),
    header: () => t('contract-form'),
  }),
  columnHelper.accessor('unitType', {
    cell: ({ getValue }) => (
      <span>
        {getValue() === 'hourly'
          ? t('hourly')
          : getRenderedCellValue(getValue())}
      </span>
    ),
    header: () => t('unit-type'),
  }),
  columnHelper.accessor('pricePerUnit', {
    cell: ({ row, getValue }) => (
      <span>
        {checkConflict(row.original) ? (
          <Tooltip
            className='font-Cairo'
            content={
              <Typography
                variant='body2'
                className=' rounded-lg bg-background-200 px-16 py-4 text-center'
              >
                {checkConflict(row.original) === 'unitMismatch'
                  ? t('unit-mismatch')
                  : t('no-supplier-assignment')}
              </Typography>
            }
            direction='down'
            arrow={false}
            tipContentHover
          >
            <span className='text-16 flex items-center justify-center text-error-500'>
              <ExleminationMark />
            </span>
          </Tooltip>
        ) : (
          getRenderedCellValue(getValue())
        )}
      </span>
    ),
    header: () => t('unit-value'),
  }),
  columnHelper.accessor('totalWorkers', {
    cell: ({ getValue }) => <span>{getRenderedCellValue(getValue())}</span>,
    header: () => t('attendance'),
  }),
  columnHelper.accessor('totalTimeSpent', {
    cell: ({ getValue }) => <span>{getRenderedCellValue(getValue())}</span>,
    header: () => (
      <Tooltip
        content={
          <Typography
            variant='body2'
            className=' rounded-lg bg-background-200 px-16 py-4 text-center'
          >
            {t('hour-calculated-at-fraction-x', { hourFraction: '30' })}
          </Typography>
        }
        direction='down'
        arrow={false}
        tipContentHover
      >
        <span className='flex items-center justify-center gap-2 '>
          {t('the-hours')}
          <span className='text-16 text-background-400'>
            <ExleminationMark />
          </span>
        </span>
      </Tooltip>
    ),
  }),
  columnHelper.accessor('totalUnitsAchieved', {
    cell: ({ getValue }) => <span>{getRenderedCellValue(getValue())}</span>,
    header: () => t('actual-productivity'),
  }),
  columnHelper.accessor('expectedProductivityTarget', {
    cell: ({ getValue }) => <span>{getRenderedCellValue(getValue())}</span>,
    header: () => t('productivity-target'),
  }),
  columnHelper.accessor('supervisionDeduction', {
    cell: ({ getValue }) => <span>{getRenderedCellValue(getValue())}</span>,
    header: () => (
      <>
        {t('supervisionDeduction')} ({currency})
      </>
    ),
  }),
  columnHelper.accessor('supervisionBonuses', {
    cell: ({ getValue }) => <span>{getRenderedCellValue(getValue())}</span>,
    header: () => (
      <>
        {t('supervisionBonuses')} ({currency})
      </>
    ),
  }),
  columnHelper.accessor('netPayment', {
    cell: ({ getValue }) => <span>{getRenderedCellValue(getValue())}</span>,
    header: () => (
      <>
        {t('netDues')} ({currency})
      </>
    ),
  }),
  columnHelper.accessor('uploadDate', {
    cell: ({ getValue }) => (
      <span>
        {getValue() ? format(new Date(getValue()), 'dd/MM/yyyy') : '-'}
      </span>
    ),
    header: () => t('upload-date'),
  }),
  columnHelper.accessor('approvers', {
    cell: ({
      getValue,
      row: {
        original: { jobInstanceId, approvers },
      },
    }) =>
      approvers?.length ? (
        <Tooltip
          // className='tablet:min-w-[335px]'
          arrow={false}
          content={
            approvers && (
              <ApprovalStatusPopover
                approvers={approvers}
                jobInstanceId={jobInstanceId}
              />
            )
          }
        >
          <div className='flex items-center justify-between gap-2'>
            <div>
              <ExleminationMark />
            </div>
            {/* TODO: update logic here to a function mapping */}
            <div className='flex w-full items-center justify-between gap-[2px]'>
              {approvers.map((item) => (
                <span
                  className={`${
                    item.activeApprover
                      ? item.approvalStatus === APPROVAL_STATUS.APPROVED ||
                        item.approvalStatus ===
                          APPROVAL_STATUS.EDITED_AND_APPROVED
                        ? 'bg-secondary-20'
                        : item.approvalStatus === APPROVAL_STATUS.PENDING ||
                          item.approvalStatus ===
                            APPROVAL_STATUS.EDITED_AND_PENDING ||
                          item.approvalStatus ===
                            APPROVAL_STATUS.REJECTED_AND_PENDING
                        ? 'bg-states-waiting'
                        : 'bg-error-400'
                      : 'bg-neutralVariant-90'
                  } h-3 w-full rounded-[10px]`}
                  key={item.id}
                />
              ))}
            </div>
          </div>
        </Tooltip>
      ) : getValue() === undefined ? (
        <Typography variant='body2' className=' whitespace-pre-wrap'>
          {t('not-applicable')}
        </Typography>
      ) : (
        <Typography variant='body2' className=' whitespace-pre-wrap'>
          {t('no-approvals-paths')}
        </Typography>
      ),
    header: () => t('approvals'),
  }),
];
